// Creating a task group (S88): the wizard's frame and steps; the source step's words are in
// source.ts and the Jira query builder's in jira.ts.
import jira from "./jira";
import sourceStep from "./source";

export default {
  newGroup: "New task group",
  back: "Back",
  next: "Next",
  create: "Create task group",
  created: (name: string) => `Task group “${name}” created`,
  toolchainsNotSet: (reason: string) => `The group was created, but its toolchains were not saved: ${reason}. Set them on the group's page.`,
  createFailed: "Could not create the task group",
  edit: {
    title: "Edit task group",
    subtitle: (name: string) => `Change where “${name}” gets its tasks, the secrets it uses and its settings. Its task type, workflow and kind of source stay.`,
    cancel: "Cancel",
    save: "Save changes",
    saved: (name: string) => `Task group “${name}” saved`,
    saveFailed: "Could not save the task group",
    loadFailed: "Could not load the task group",
    workflowFallback: "The group's workflow",
    sourceDescription: "Change which items become tasks, the secret that reads them and how often the group checks. The kind of source stays.",
    reviewDescription: "Check the changes, then save. Tasks already queued keep running under the group's new settings.",
  },
  progress: "Steps",
  stepOf: (n: number, total: number) => `Step ${n} of ${total}`,
  steps: {
    type: { label: "Task type", title: "What kind of work is it?", description: "The task type decides which workflows you can choose from." },
    workflow: { label: "Workflow", title: "Choose a workflow", description: "Every task in the group runs through this workflow. The group keeps the version you choose until you upgrade it." },
    source: { label: "Source", title: "Where do tasks come from?", description: "Add tasks yourself, or let the group pick up issues from GitHub, Jira or Asana as they appear." },
    access: { label: "Secrets", title: "Which secrets does it use?", description: "The workflow needs these to work. The broker uses them for the agent; the agent never sees their values." },
    review: { label: "Review", title: "Name it and check", description: "Check what you chose, then create the group. You can pause it or change its settings later." },
  },
  problems: {
    type: "Choose a task type to go on.",
    workflow: "Choose a workflow to go on.",
    repo: "Enter the repository as owner/name.",
    project: "Choose at least one Jira project.",
    jql: "Write a query (up to 2,000 characters).",
    asanaProject: "Enter the Asana project's id (digits).",
    credential: "Choose the secret to read the source with.",
    cadence: "Choose how often to check (1 to 1,440 minutes).",
    repositories: "Fix the repositories below.",
    access: (labels: string) => `Choose a secret for: ${labels}.`,
    name: "Give the group a name.",
    parallel: "Parallel tasks must be between 1 and 32.",
  },
  type: {
    label: "Task types",
    loadFailed: "Could not load the task types",
    none: "No task types yet",
    noneBody: "An admin adds task types and publishes workflows for them first.",
  },
  workflow: {
    label: "Workflows",
    loadFailed: "Could not load the workflows",
    search: "Search workflows",
    searchPlaceholder: "Search by name or description",
    noMatch: (q: string) => `No workflow matches “${q}”`,
    noMatchBody: "Try other words, or clear the search to see every workflow for this type.",
    chosen: "Your choice",
    pages: "Workflow pages",
    previous: "Previous",
    next: "Next",
    page: (n: number) => `Page ${n}`,
    none: "No workflow for this type yet",
    noneBody: "No published workflow does this kind of work. Choose another type, or ask an admin to publish one.",
    needs: "Needs",
    needsNothing: "Needs no secrets",
    version: (v: number, steps: number) => `Version ${v} · ${steps} ${steps === 1 ? "step" : "steps"}`,
  },
  secrets: {
    add: "Add a secret",
    addAnother: "Add another secret",
    choose: "Choose a secret…",
    needsDevice: "Secrets are sealed and signed by a linked device.",
    linkDevice: "Link this browser",
    saved: (name: string) => `Secret “${name}” added`,
  },
  access: {
    none: "Nothing to bind",
    noneBody: "This workflow needs no secrets.",
    vm: "Each task runs in its own virtual machine.",
    oauth: "Connected from your phone when a step needs it.",
    platformKey: "Use the platform key",
    required: "Required",
    optional: "Optional",
    use: "Use",
    noMatch: "You have no secret of this kind yet.",
    toolchains: "Toolchains",
    toolchainsHint: "Installed in the group's own worker image, so every step has them. Those the workflow requires are ticked. You can change them and add a setup script on the group's page.",
  },
  review: {
    name: "Name",
    nameHint: "Shown in the apps and on notifications.",
    parallel: "Parallel tasks",
    parallelHint: "How many of its tasks may run at once.",
    edit: "Edit",
    editLabel: (label: string) => `Edit ${label}`,
    workflowVersion: (name: string, v: number) => `${name} · version ${v}`,
    source: "Source",
    sourceVia: (secret: string, cadence: string) => `With ${secret} · ${cadence}`,
    repositories: "Repositories",
    noRepositories: "None besides the source's own",
    secrets: "Secrets",
    noSecrets: "None",
    toolchains: "Toolchains",
    noToolchains: "None: the platform's image",
  },
  ...sourceStep,
  jira,
};
